import React, { useEffect, useState } from "react";
import PageTableLayout from "../../components/Pagetablelayout";
import axios from "axios";
import { Space, Button, Modal, Form, Input, Select, Row, Col, Popconfirm } from "antd";

export default function CountriesPage() {
  const [countries, setCountries] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]); // options for tags multi-select
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [form] = Form.useForm();

  const token = localStorage.getItem("jwt");
  const API_BASE = process.env.REACT_APP_API_URL || "https://mdgholidaydreams.com/api";

  const headersWithAuth = (extra = {}) =>
    token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };

  // Fetch tags from tags table to populate multi-select options
  const fetchTagsOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tags/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      // map to Select options: { label, value }
      const opts = (data || []).map((t) => ({
        label: t.name,
        value: t.name, // use name as value; change to t.id if backend expects ids
      }));
      setTagsOptions(opts);
    } catch (err) {
      console.error("Fetch tags options failed:", err.response?.data ?? err);
    }
  };

  const fetchCountries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/countries/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      const parsed = (data || []).map((c) => ({
        ...c,
        offerings: Array.isArray(c.offerings) ? c.offerings : tryParseJsonArray(c.offerings),
        tag: Array.isArray(c.tag) ? c.tag : tryParseJsonArray(c.tag),
        active: Number(c.active),
      }));
      setCountries(parsed);
    } catch (err) {
      console.error("Fetch countries failed:", err.response?.data ?? err);
    }
    setLoading(false);
  };

  // helper to try parse a JSON array string, fallback to splitting by comma
  function tryParseJsonArray(value) {
    if (!value && value !== 0) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return Array.isArray(parsed) ? parsed : String(value).split(",").map((s) => s.trim()).filter(Boolean);
    } catch {
      return String(value).split(",").map((s) => s.trim()).filter(Boolean);
    }
  }

  useEffect(() => {
    fetchTagsOptions();
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    setEditingCountry(null);
    form.resetFields();
    form.setFieldsValue({ active: 1, offerings: [], tag: [] });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCountry(record);
    form.setFieldsValue({
      ...record,
      offerings: Array.isArray(record.offerings) ? record.offerings : tryParseJsonArray(record.offerings),
      tag: Array.isArray(record.tag) ? record.tag : tryParseJsonArray(record.tag),
      active: Number(record.active),
    });
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Normalize multi-select fields
      if (!Array.isArray(values.offerings)) {
        values.offerings = values.offerings ? [values.offerings] : [];
      }
      if (!Array.isArray(values.tag)) {
        values.tag = values.tag ? [values.tag] : [];
      }
      values.active = Number(values.active ?? 1);

      // Build explicit payload so backend doesn't receive undefined fields
      const payload = {
        name: values.name,
        code: values.code,
        tag: values.tag, // array of tag names or ids depending on your backend
        description: values.description ?? null,
        image_url: values.image_url ?? null,
        offerings: values.offerings, // array
        best_time: values.best_time ?? null,
        currency: values.currency ?? null,
        capital: values.capital ?? null,
        note: values.note ?? null,
        active: values.active,
      };

      console.log("Payload being sent:", payload);

      if (editingCountry) {
        await axios.put(`${API_BASE}/countries/${editingCountry.id}/`, payload, {
          headers: headersWithAuth({ "Content-Type": "application/json" }),
        });
      } else {
        await axios.post(`${API_BASE}/countries/`, payload, {
          headers: headersWithAuth({ "Content-Type": "application/json" }),
        });
      }

      form.resetFields();
      setIsModalVisible(false);
      setEditingCountry(null);
      fetchCountries();
    } catch (err) {
      console.error("Save failed:", err.response?.data ?? err);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingCountry(null);
    form.resetFields();
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Code", dataIndex: "code", key: "code" },
    {
      title: "Tags",
      dataIndex: "tag",
      key: "tag",
      render: (t) =>
        Array.isArray(t)
          ? t.map((item, i) => (
              <span
                key={i}
                style={{
                  marginRight: 6,
                  background: "#e6f7ff",
                  padding: "2px 6px",
                  borderRadius: 4,
                  display: "inline-block",
                }}
              >
                {item}
              </span>
            ))
          : "",
    },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      render: (url) => url && <img src={url} alt="" style={{ width: 80, borderRadius: 4 }} />,
    },
    {
      title: "Offerings",
      dataIndex: "offerings",
      key: "offerings",
      render: (o) =>
        Array.isArray(o)
          ? o.map((item, i) => (
              <span
                key={i}
                style={{
                  marginRight: 6,
                  background: "#f0f0f0",
                  padding: "2px 6px",
                  borderRadius: 4,
                  display: "inline-block",
                }}
              >
                {item}
              </span>
            ))
          : "",
    },
    { title: "Best Time", dataIndex: "best_time", key: "best_time" },
    { title: "Currency", dataIndex: "currency", key: "currency" },
    { title: "Capital", dataIndex: "capital", key: "capital" },
    { title: "Note", dataIndex: "note", key: "note" },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active) => (Number(active) === 1 ? "Active" : "Inactive"),
    },
    { title: "Created By", dataIndex: "created_by", key: "created_by" },
    { title: "Updated By", dataIndex: "updated_by", key: "updated_by" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>Edit</Button>

          <Popconfirm
            title="Deactivate this country?"
            okText="Yes"
            cancelText="No"
            onConfirm={async () => {
              try {
                await axios.patch(`${API_BASE}/countries/${record.id}/`, {}, {
                  headers: headersWithAuth(),
                });
                fetchCountries();
              } catch (err) {
                console.error("Deactivate failed:", err.response?.data ?? err);
              }
            }}
          >
            <Button danger>Deactivate</Button>
          </Popconfirm>

          <Popconfirm
            title="Delete this country?"
            okText="Yes"
            cancelText="No"
            onConfirm={async () => {
              try {
                await axios.delete(`${API_BASE}/countries/${record.id}/`, {
                  headers: headersWithAuth(),
                });
                fetchCountries();
              } catch (err) {
                console.error("Delete failed:", err.response?.data ?? err);
              }
            }}
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTableLayout
        breadcrumb={["Admin", "Countries"]}
        title="Manage Countries"
        addButtonLabel="Add Country"
        onAdd={handleAdd}
        columns={columns}
        dataSource={countries}
        loading={loading}
        onRefresh={fetchCountries}
      />

      <Modal
        title={editingCountry ? "Edit Country" : "Add New Country"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Country Name" rules={[{ required: true, message: "Please enter a country name" }]}>
                <Input />
              </Form.Item>

              <Form.Item name="code" label="Country Code" rules={[{ required: true, message: "Please enter a country code" }]}>
                <Input />
              </Form.Item>

              <Form.Item name="tag" label="Tags">
                <Select
                  mode="multiple"
                  options={tagsOptions}
                  style={{ width: "100%" }}
                  placeholder="Select or type tags"
                  tokenSeparators={[","]}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="active" label="Status" rules={[{ required: true, message: "Please select status" }]}>
                <Select placeholder="Select status">
                  <Select.Option value={1}>Active</Select.Option>
                  <Select.Option value={0}>Inactive</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="best_time" label="Best Time">
                <Input />
              </Form.Item>

              <Form.Item name="currency" label="Currency">
                <Input />
              </Form.Item>

              <Form.Item name="capital" label="Capital">
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>

              <Form.Item name="image_url" label="Image URL">
                <Input />
              </Form.Item>

              <Form.Item name="offerings" label="Offerings">
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  placeholder="Enter offerings (press Enter after each)"
                  tokenSeparators={[","]}
                />
              </Form.Item>

              <Form.Item name="note" label="Note">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
